/**
 * ChatHeaderV2 - Mantine-based chat header component
 *
 * Displays entity information (worker or company) with optional
 * contact details, status badges, and action buttons.
 */

import { useRef, type ReactNode } from 'react';
import { isMobile } from 'react-device-detect';
import {
  Anchor,
  Avatar,
  Group,
  Stack,
  Text,
  Button,
  ActionIcon,
  Badge,
  Tooltip,
} from '@mantine/core';
import { IoMdClose } from 'react-icons/io';
import { FiUser } from 'react-icons/fi';
import { MdEmail } from 'react-icons/md';
import { ImPhone } from 'react-icons/im';
import { FaLocationDot } from 'react-icons/fa6';
import { BsBriefcaseFill } from 'react-icons/bs';
import useAvatarImageFallback from '@hooks/useAvatarImageFallback';
import { ChatEntity, ChatJobInfo, ChatStatusBadge } from '@th-types/chat.type';
import {
  ANNOUNCEMENT_THREAD_LABEL,
  DIRECT_THREAD_LABEL,
} from '@constants/directChat';
import { formatOpenDateRange } from '@utils/DateUtils';
import DATE_FORMATS from '@constants/dateFormat';

interface ChatHeaderV2Props {
  /** The entity to display (worker for company side, company for worker side) */
  entity: ChatEntity;
  /** Optional job information */
  jobInfo?: ChatJobInfo;
  /** Show contact details (email, phone) - typically only for company viewing worker */
  showContactInfo?: boolean;
  /** Whether this is a direct message (shows different subtitle) */
  isDirectMessage?: boolean;
  /** Platform announcement thread (read-only Trusted Herd Admin label) */
  isAnnouncement?: boolean;
  /** Status badges to display (e.g., "HIRED", "BRAND AMBASSADOR") */
  statusBadges?: ChatStatusBadge[];
  /** Callback when VIEW PROFILE is clicked */
  onViewProfile?: () => void;
  /** Callback when close button is clicked */
  onClose: () => void;
}

function ChatHeaderV2({
  entity,
  jobInfo,
  showContactInfo = false,
  isDirectMessage = false,
  isAnnouncement = false,
  statusBadges = [],
  onViewProfile,
  onClose,
}: ChatHeaderV2Props) {
  const imageRef = useRef<HTMLImageElement>(null);
  useAvatarImageFallback(imageRef);

  let threadSubtitle: ReactNode = null;
  if (isAnnouncement) {
    threadSubtitle = (
      <Text size="sm" c="dimmed" fs="italic">
        {ANNOUNCEMENT_THREAD_LABEL}
      </Text>
    );
  } else if (isDirectMessage) {
    threadSubtitle = (
      <Text size="sm" c="dimmed" fs="italic">
        {DIRECT_THREAD_LABEL}
      </Text>
    );
  }

  return (
    <Group p="md" gap="md" wrap="nowrap" align="flex-start">
      {/* Avatar */}
      <Avatar
        ref={imageRef}
        src={entity.profileImageUrl}
        alt={entity.name}
        size={isMobile ? 60 : 80}
        radius="50%"
      />

      {/* Info Section */}
      <Stack gap={4} flex={1} style={{ minWidth: 0 }}>
        {/* Name and badges */}
        <Group gap="xs" wrap="wrap">
          <Tooltip
            label={onViewProfile ? 'View Profile' : undefined}
            disabled={!onViewProfile}
          >
            <Text
              fw={600}
              size="lg"
              style={{
                cursor: onViewProfile ? 'pointer' : 'default',
              }}
              onClick={onViewProfile}
            >
              {entity.name}
            </Text>
          </Tooltip>
          {statusBadges.map((badge) => (
            <Badge
              key={badge.label}
              size="sm"
              variant="light"
              color={badge.color || 'gray'}
            >
              {badge.label}
            </Badge>
          ))}
        </Group>

        {/* Subtitle - Announcement, direct message, or job info */}
        {threadSubtitle}
        {!threadSubtitle && (
          <>
            {/* Position + Event Name */}
            {(jobInfo?.jobPosition || jobInfo?.eventName) && (
              <Group gap="xs">
                <BsBriefcaseFill
                  size={12}
                  color="var(--mantine-color-dimmed)"
                />
                {jobInfo.jobDetailUrl ? (
                  <Anchor
                    href={jobInfo.jobDetailUrl}
                    size="sm"
                    truncate
                    target="_blank"
                  >
                    {[jobInfo.jobPosition, jobInfo.eventName]
                      .filter(Boolean)
                      .join(' - ')}
                  </Anchor>
                ) : (
                  <Text size="sm" c="dimmed" truncate>
                    {[jobInfo.jobPosition, jobInfo.eventName]
                      .filter(Boolean)
                      .join(' - ')}
                  </Text>
                )}
              </Group>
            )}

            {/* Job dates */}
            {jobInfo?.startDate && (
              <Group gap="xs">
                <Text size="sm" c="dimmed">
                  {formatOpenDateRange(
                    new Date(jobInfo.startDate),
                    jobInfo.endDate ? new Date(jobInfo.endDate) : null,
                    DATE_FORMATS.DATE_FORMAT
                  )}
                </Text>
              </Group>
            )}

            {/* Contact info (email, phone) */}
            {showContactInfo && entity.cellPhone && (
              <Group gap="xs">
                <ImPhone size={12} color="var(--mantine-color-dimmed)" />
                <Text size="sm" c="dimmed">
                  {entity.cellPhone}
                </Text>
              </Group>
            )}
            {showContactInfo && entity.email && (
              <Group gap="xs">
                <MdEmail size={14} color="var(--mantine-color-dimmed)" />
                <Text size="sm" c="dimmed" truncate>
                  {entity.email}
                </Text>
              </Group>
            )}

            {/* Job name / address (legacy fields, kept for company-side) */}
            {jobInfo?.jobName && (
              <Group gap="xs">
                <BsBriefcaseFill
                  size={12}
                  color="var(--mantine-color-dimmed)"
                />
                <Text size="sm" c="dimmed" truncate>
                  {jobInfo.jobName}
                </Text>
              </Group>
            )}
            {jobInfo?.address && (
              <Group gap="xs">
                <FaLocationDot size={12} color="var(--mantine-color-dimmed)" />
                <Text size="sm" c="dimmed" truncate>
                  {jobInfo.address}
                </Text>
              </Group>
            )}
          </>
        )}
      </Stack>

      {/* Action Buttons */}
      <Stack gap="xs" align="flex-end">
        <ActionIcon
          variant="white"
          color="grey"
          onClick={onClose}
          aria-label="Close chat"
          size="lg"
        >
          <IoMdClose size={20} />
        </ActionIcon>
        {onViewProfile && (
          <Button variant="light" size="sm" onClick={onViewProfile}>
            {isMobile ? <FiUser size={18} /> : 'VIEW PROFILE'}
          </Button>
        )}
      </Stack>
    </Group>
  );
}

export default ChatHeaderV2;
